import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { X, RotateCcw, Lock, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { HandwrittenReveal } from '@/components/reveal/MessageRevealEffects';
import type { TreslogEntry } from '@/types/treslog';

interface TreslogEntryRevealDialogProps {
  entry: TreslogEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TreslogEntryRevealDialog({ entry, open, onOpenChange }: TreslogEntryRevealDialogProps) {
  const [revealKey, setRevealKey] = useState(0);
  const [revealDone, setRevealDone] = useState(false);

  const handleComplete = useCallback(() => setRevealDone(true), []);
  const handleReplay = () => {
    setRevealDone(false);
    setRevealKey((k) => k + 1);
  };

  // Reset state when dialog opens
  const handleOpenChange = (value: boolean) => {
    if (value) {
      setRevealDone(false);
      setRevealKey((k) => k + 1);
    }
    onOpenChange(value);
  };

  if (!entry) return null;

  const photos = entry.photo_urls || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 gap-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card border-b border-border/50 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg text-foreground">{entry.title}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-muted-foreground">
                {format(new Date(entry.created_at), 'MMMM d, yyyy')}
              </span>
              <Badge variant={entry.is_public ? 'secondary' : 'outline'} className="gap-1 text-xs">
                {entry.is_public ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                {entry.is_public ? 'Shared' : 'Private'}
              </Badge>
            </div>
          </div>

          <AnimatePresence>
            {revealDone && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
                <Button variant="ghost" size="sm" onClick={handleReplay} className="gap-1.5 text-muted-foreground">
                  <RotateCcw className="h-3.5 w-3.5" />
                  Replay
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="px-6 pt-4 flex gap-3 overflow-x-auto">
            {photos.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt=""
                className="h-32 w-32 object-cover rounded-lg flex-shrink-0 border border-border/30"
              />
            ))}
          </div>
        )}

        {/* Handwritten reveal of the content */}
        <div className="px-6 py-8 min-h-[200px]">
          <HandwrittenReveal
            key={revealKey}
            message={entry.content}
            className=""
            onComplete={handleComplete}
            skipped={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
